import type { PolicyResult } from '@content-search/domain';
import { Badge, BadgeSize } from '@soteria/design-system-ve';

import * as styles from './PolicyDisplay.css';
import { PolicyOverallStatus } from './PolicyOverallStatus';
import { PolicySection } from './PolicySection';

interface PolicyDisplayProps {
  policyResult: PolicyResult;
  /** Whether to show compact view (violated policy badges) or detailed view (with grouped sections) */
  compact?: boolean;
}

/**
 * Component to display policy evaluation results in content cards
 *
 * In compact mode: Shows individual violated policy badges (similar to "Triggered Policies" in audit view)
 * In detailed mode: Shows overall status with grouped "Triggered Policies" and "Nontriggered Policies" sections
 */
export function PolicyDisplay({ policyResult, compact = false }: PolicyDisplayProps) {
  const { isCompliant, totalPoliciesEvaluated, allowedCount, rejectedCount, policyEvaluations } = policyResult;

  // Split policies into triggered (rejected) and nontriggered (allowed)
  const triggeredPolicies = policyEvaluations.filter(evaluation => !evaluation.isAllowed);
  const nontriggeredPolicies = policyEvaluations.filter(evaluation => evaluation.isAllowed);

  // If compact view, show violated policies as badges (similar to audit view's "Triggered Policies")
  if (compact) {
    // If no violations, show "None"
    if (triggeredPolicies.length === 0) {
      return (
        <div className={styles.policyDisplayCompact}>
          <Badge size={BadgeSize.Small} text="None" />
        </div>
      );
    }

    // Show triggered policy badges
    return (
      <div className={styles.policyBadges}>
        {triggeredPolicies.map(evaluation => (
          <Badge key={evaluation.policyId} size={BadgeSize.Small} text={evaluation.policyName} />
        ))}
      </div>
    );
  }

  // Detailed view - grouped by triggered/nontriggered similar to audit flow
  return (
    <div className={styles.policyDisplay}>
      <PolicyOverallStatus
        allowedCount={allowedCount}
        isCompliant={isCompliant}
        rejectedCount={rejectedCount}
        totalPoliciesEvaluated={totalPoliciesEvaluated}
      />

      {/* Triggered Policies Section */}
      {triggeredPolicies.length > 0 && <PolicySection showTriggeredBadge policies={triggeredPolicies} title="Triggered Policies" />}

      {/* Nontriggered Policies Section */}
      {nontriggeredPolicies.length > 0 && <PolicySection policies={nontriggeredPolicies} showTriggeredBadge={false} title="Nontriggered Policies" />}
    </div>
  );
}
